import { useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import { getOnboardingDevOverride, useOnboardingGate } from "./useOnboardingGate";
import { useOnboardingStore } from "./useOnboardingStore";
import { resetOnboardingState } from "./api";
import { trackOnboarding } from "./analytics";
import WelcomeModal from "./WelcomeModal";
import ChecklistLauncher from "./checklist/ChecklistLauncher";
import { useChecklistDetection } from "./checklist/useChecklistDetection";

/**
 * Single mount point for all onboarding UI, rendered once in the App.jsx
 * layout (main window only — popout windows portal componentMap widgets and
 * never render this tree).
 *
 * POPOUT SAFETY RULE: nothing under src/onboarding/ may be imported from
 * src/widget-components/** — onboarding UI must never reach popout windows.
 *
 * The welcome flow (Phase 4) and checklist (Phase 5) mount here.
 */
export function OnboardingGate() {
  const { phase, showChecklist } = useOnboardingGate();
  const begin = useOnboardingStore((s) => s.begin);
  const { authenticated } = useAuth();

  // Dev/testing: ?onboarding=reset wipes the saved state server-side, then
  // reloads into ?onboarding=force so the flow re-runs as a fresh trialist.
  // Non-production builds only (see getOnboardingDevOverride).
  const [resetting] = useState(() => getOnboardingDevOverride() === "reset");
  useEffect(() => {
    if (!resetting || !authenticated) return;
    resetOnboardingState()
      .catch((error) => console.error("[onboarding] dev reset failed:", error))
      .finally(() => {
        const url = new URL(window.location.href);
        url.searchParams.set("onboarding", "force");
        window.location.replace(url.toString());
      });
  }, [resetting, authenticated]);

  // Passive task detection runs whenever the checklist is live.
  useChecklistDetection(showChecklist);

  // First ever login for an eligible trialist: persist the initial state
  // immediately so the trigger survives the tabs signal being destroyed and
  // the flow becomes resume-driven from here on.
  useEffect(() => {
    if (phase === "trigger") {
      begin();
    } else if (phase === "resume") {
      trackOnboarding("resumed");
    }
    // trackOnboarding intentionally fires once per phase settlement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (resetting || phase === "waiting" || phase === "ineligible") return null;

  // "trigger" renders the modal too — begin() persists the initial state on
  // the same render cycle, after which the gate settles into "resume".
  const showWelcome = phase === "trigger" || phase === "resume";

  return (
    <>
      {showWelcome && <WelcomeModal />}
      {showChecklist && <ChecklistLauncher />}
      {import.meta.env.DEV && (
        <div
          className="hidden"
          data-onboarding-phase={phase}
          data-onboarding-checklist={String(showChecklist)}
        />
      )}
    </>
  );
}
